import { useRef } from "react";
import { InputAdornment, TextField, IconButton, Button, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import toast from "@/lib/toast";
import { fileExtensions } from "@/lib/methodUtils";

type FileItem =
    | File
    | {
        url: string;
        name: string;
        extension?: string;
        id?: number;
        isExisting: true;
    };

const TYPE_LIMITS = {
    image: 3,
    video: 1,
    pdf: 1,
};

interface MultipleFileProps {
    field: any;
    label?: string;
    onFileChange?: (
        files: FileItem[],
        reason?: "invalid" | "removed" | "selected"
    ) => void;
    maxSelectedFiles?: number;
    maxImageSize?: number;
    maxVideoSize?: number;
    maxDocumentSize?: number;
    accept?: string;
    setRemoveFileIds?: React.Dispatch<React.SetStateAction<number[]>>;
    required?: boolean;
    disabled?: boolean;
    error?: boolean;
    helperText?: string;
}

const MultipleFileInput = ({
    field,
    label,
    onFileChange,
    maxImageSize = 5 * 1024 * 1024,
    maxVideoSize = 15 * 1024 * 1024,
    maxDocumentSize = 5 * 1024 * 1024,
    maxSelectedFiles = 5,
    accept = "",
    setRemoveFileIds,
    required = false,
    disabled = false,
    error = false,
    helperText,
}: MultipleFileProps) => {
    const inputRef = useRef<HTMLInputElement | null>(null);

    const files: FileItem[] = field?.value || [];

    const fileCountText = files.length > 0
        ? `${files.length} / ${maxSelectedFiles} files`
        : "";

    const getExtension = (file: any) =>
        (file?.extension || file?.name?.split(".").pop() || "").toLowerCase();

    const getFileType = (file: any) => {
        const ext = getExtension(file);

        if (file?.type?.startsWith("image/") || fileExtensions.image.has(ext))
            return "image";

        if (file?.type?.startsWith("video/") || fileExtensions.video.has(ext))
            return "video";

        if (file?.type === "application/pdf" || fileExtensions.pdf.has(ext))
            return "pdf";

        return "other";
    };

    const getFileIcon = (file: any) => {
        const type = getFileType(file);

        if (type === "image")
            return <ImageIcon fontSize="small" className="text-blue-500" />;

        if (type === "video")
            return <VideoFileIcon fontSize="small" className="text-purple-500" />;

        if (type === "pdf")
            return <PictureAsPdfIcon fontSize="small" className="text-red-500" />;

        return <InsertDriveFileIcon fontSize="small" className="text-gray-500" />;
    };

    const formatFileSize = (size?: number) => {
        if (!size) return "";

        if (size < 1024 * 1024) {
            return `${(size / 1024).toFixed(1)} KB`;
        }

        return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    };

    const isAcceptedFile = (file: File) => {
        if (!accept) return true;

        const acceptedTypes = accept
            .split(",")
            .map((item) => item.trim().toLowerCase());

        const fileExtension = `.${file.name.split(".").pop()?.toLowerCase()}`;
        const mimeType = file.type.toLowerCase();

        return acceptedTypes.some((type) => {
            if (type.startsWith(".")) {
                return fileExtension === type;
            }
            if (type.endsWith("/*")) {
                return mimeType.startsWith(type.replace("*", ""));
            }
            return mimeType === type;
        });
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = Array.from(e.target.files || []);
        if (!selectedFiles.length) return;

        let updatedFiles = [...files];
        const warnings = new Set<string>();

        let counts = {
            image: updatedFiles.filter((f) => getFileType(f) === "image").length,
            video: updatedFiles.filter((f) => getFileType(f) === "video").length,
            pdf: updatedFiles.filter((f) => getFileType(f) === "pdf").length,
        };

        for (let file of selectedFiles) {
            if (!isAcceptedFile(file)) {
                warnings.add("File type is not supported");
                continue;
            }

            const type = getFileType(file);

            if (type === "other") {
                warnings.add("File type is not supported");
                continue;
            }

            let allowedSize = maxDocumentSize;
            let fileTypeName = "File";

            if (type === "image") {
                allowedSize = maxImageSize;
                fileTypeName = "Image";
            } else if (type === "video") {
                allowedSize = maxVideoSize;
                fileTypeName = "Video";
            } else if (type === "pdf") {
                allowedSize = maxDocumentSize;
                fileTypeName = "PDF";
            }

            if (file.size > allowedSize) {
                warnings.add(
                    `${fileTypeName} size should be less than ${formatFileSize(
                        allowedSize
                    )}`
                );
                continue;
            }

            if (updatedFiles.length >= maxSelectedFiles) {
                warnings.add(`Maximum ${maxSelectedFiles} files allowed`);
                break;
            }

            if (counts[type] >= TYPE_LIMITS[type]) {
                warnings.add(`Only ${TYPE_LIMITS[type]} ${type}(s) allowed`);
                continue;
            }

            const isDuplicate = updatedFiles.some((existing: any) => {
                if (existing instanceof File) {
                    return (
                        existing.name === file.name &&
                        existing.size === file.size
                    );
                }

                return (
                    existing.isExisting &&
                    existing.name === file.name
                );
            });

            if (isDuplicate) {
                warnings.add(`${file.name} is already selected`);
                continue;
            }

            updatedFiles.push(file);
            counts[type]++;
        }

        warnings.forEach((msg) => toast.warning(msg));

        field.onChange(updatedFiles);
        onFileChange?.(
            updatedFiles,
            warnings.size ? "invalid" : "selected"
        );

        if (inputRef.current) inputRef.current.value = "";
    };

    const removeFile = (index: number) => {
        const fileToRemove: any = files[index];

        if (fileToRemove?.isExisting && fileToRemove?.id && setRemoveFileIds) {
            setRemoveFileIds((prev) => [
                ...new Set([...prev, fileToRemove.id])
            ]);
        }

        const updated = files.filter((_, i) => i !== index);
        field.onChange(updated);
        onFileChange?.(updated, "removed");

        if (inputRef.current) {
            inputRef.current.value = "";
        }
    };

    const clearAll = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();

        if (setRemoveFileIds) {
            const idsToRemove = files
                .filter((f: any) => f?.isExisting && f?.id)
                .map((f: any) => f.id);

            if (idsToRemove.length) {
                setRemoveFileIds((prev) => [
                    ...new Set([...prev, ...idsToRemove])
                ]);
            }
        }

        field.onChange([]);
        onFileChange?.([], "removed");

        if (inputRef.current) {
            inputRef.current.value = "";
        }
    };

    return (
        <div className="w-full">
            {/* Input */}
            <TextField
                label={label}
                variant="standard"
                fullWidth
                value={fileCountText}
                onClick={(e) => {
                    const target = e.target as HTMLElement;

                    if (target.closest("button")) {
                        return;
                    }

                    inputRef.current?.click();
                }}
                required={required}
                disabled={disabled}
                error={error}
                helperText={helperText}
                slotProps={{
                    input: {
                        readOnly: true,
                        endAdornment: files.length > 0 ? (
                            <InputAdornment position="end">
                                <Button
                                    variant="text"
                                    onClick={clearAll}
                                    className="text-red-500 cursor-pointer"
                                >
                                    Clear All
                                </Button>
                            </InputAdornment>
                        ) : (
                            <InputAdornment position="end">
                                <img src="/assets/media/paperclip.svg" alt="attach" width={16} />
                            </InputAdornment>
                        ),
                    }
                }}
            />

            {/* Hidden Input */}
            <input
                type="file"
                multiple
                hidden
                ref={inputRef}
                accept={accept}
                onChange={handleChange}
                disabled={files.length >= maxSelectedFiles}
            />

            {/* File List */}
            {files.length > 0 && (
                <div className="mt-3 max-h-40 overflow-y-auto space-y-2">
                    {files.map((file: any, index: number) => (
                        <div
                            key={file.id ?? `${file.name}-${index}`}
                            className="flex items-center justify-between border border-gray-200 rounded-lg px-3 py-2"
                        >
                            <div className="flex items-center gap-2 min-w-0">
                                {getFileIcon(file)}

                                <div className="min-w-0">
                                    <Tooltip title={file.name || file.url?.split("/").pop()}>
                                        <p className="truncate max-w-[180px] text-sm font-medium">
                                            {file.name || file.url?.split("/").pop()}
                                        </p>
                                    </Tooltip>
                                    <p className="text-xs text-gray-500">
                                        {file.size ? formatFileSize(file.size) : ""}
                                    </p>
                                </div>
                            </div>

                            <IconButton
                                size="small"
                                onClick={() => removeFile(index)}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MultipleFileInput;